import React, { useCallback } from 'react'

import { Box, Text } from '@chakra-ui/react'
import { useDispatch, useSelector } from 'react-redux'
import { useDebouncedCallback } from 'use-debounce'

import NoSearchResults from './NoSearchResults'
import SearchOption from './SearchOption'
import SearchOptions from './SearchOptions'
import SearchSelect from './SearchSelect'
import SearchSelectedTags from './SearchSelectedTags'
import AnonymousPlaceholder from '../../../components/ui/AnonymousPlaceholder'
import {
  clearConstituentOptions,
  loadConstituentOptions,
  paginateConstituentOptions,
} from '../../../redux/features/constituentsSlice'
import { convertDistrictToNumeric, hasPermission } from '../../../utils'

const ConstituentSelect = ({
  onSelect = () => {},
  onRemove = () => {},
  onClear = () => {},
  filters = {},
  initialSelected,
  inputProps,
  showTags = true,
  enableClear,
}) => {
  const dispatch = useDispatch()
  const searchLoading = useSelector(state => state.constituents.options_loading)
  const user = useSelector(state => state.auth.user)
  const constituentOptions =
    useSelector(state => state.constituents.options) || []

  const selectedDistrict = !hasPermission(
    user,
    'directory.constituent.view_all_constituents'
  )
    ? [user.district]
    : []

  filters = selectedDistrict.length
    ? { ...filters, district__in: selectedDistrict }
    : filters

  const debouncedHandleChange = useDebouncedCallback(e => {
    dispatch(clearConstituentOptions())
    dispatch(
      loadConstituentOptions({
        ...filters,
        value: e.target.value,
      })
    )
  }, 200)

  const loadOptions = useCallback(
    value => {
      dispatch(clearConstituentOptions())
      dispatch(
        loadConstituentOptions({
          ...filters,
          value: value,
        })
      )
    },
    [filters]
  )

  const loadMoreResults = useCallback(() => {
    dispatch(paginateConstituentOptions())
  }, [])

  const filterOptions = (searchTerm, selectedOptions) => {
    let unselectedOptions = constituentOptions.filter(
      ({ id }) => !selectedOptions.find(({ value }) => value.id === id)
    )

    if (searchTerm)
      unselectedOptions = unselectedOptions.filter(({ name }) =>
        name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    if (unselectedOptions.length === 0) return null
    return unselectedOptions
  }

  return (
    <SearchSelect>
      <SearchOptions
        loadOptions={loadOptions}
        loadMoreOptions={loadMoreResults}
        onClear={onClear}
        onSearch={debouncedHandleChange}
        enableClear={enableClear}
        isLoading={searchLoading}
        initialSelected={initialSelected}
        inputProps={inputProps}
      >
        {({ searchTerm, selectedOptions }) =>
          filterOptions(searchTerm, selectedOptions)?.map(
            (constituent, index) => {
              let identifier =
                constituent.home_address?.full_address ||
                constituent.business_address?.full_address ||
                constituent.contact_info.find(c => c.is_preferred_method)
                  ?.contact_data ||
                constituent.contact_info?.[0]?.contact_data
              return (
                <SearchOption
                  key={`constituent-option-${index}`}
                  option={{
                    label: constituent.name || identifier || 'Anonymous',
                    value: constituent,
                  }}
                  onSelect={value =>
                    onSelect({
                      ...value,
                      identifier: constituent.name || identifier,
                    })
                  }
                >
                  <Box>
                    <Text>
                      {constituent.name || constituent.preferred_name || (
                        <AnonymousPlaceholder />
                      )}
                    </Text>
                    <Text variant='subheading-sm' casing='initial'>
                      {identifier}
                    </Text>
                    {hasPermission(
                      user,
                      'directory.constituent.view_all_constituents'
                    ) && (
                      <Text variant='subheading-sm' casing='initial'>
                        {`District 
                        ${convertDistrictToNumeric(constituent.district)}`}
                      </Text>
                    )}
                  </Box>
                </SearchOption>
              )
            }
          ) || <NoSearchResults> No constituent found </NoSearchResults>
        }
      </SearchOptions>
      {showTags && <SearchSelectedTags onRemove={onRemove} />}
    </SearchSelect>
  )
}

export default ConstituentSelect
